import { useCallback } from "react";
import { error as logError } from "../utils/logger";
import { updateCart } from "../services/cartService";
import { useCartContext } from "../context/CartContext";

/**
 * Hook to handle cart updates with date formatting.
 * Consolidates the repeated cart update logic used across multiple components.
 *
 * @returns Object containing the handleUpdateCart function
 */
export function useUpdateCartHandler() {
    const { reloadCart } = useCartContext();

    /**
     * Updates the cart with new dates and optionally a new address.
     *
     * @param selectedDates - Array of ISO date strings
     * @param address - Optional delivery address
     */
    const handleUpdateCart = useCallback(
        async (selectedDates: string[], address?: string) => {
            if (selectedDates.length === 0) return;

            try {
                // Format dates to Czech locale for backend
                const formDates = selectedDates.map((d) =>
                    new Date(d).toLocaleDateString("cs")
                );

                await updateCart(
                    selectedDates[0],
                    selectedDates[selectedDates.length - 1],
                    formDates,
                    address
                );

                await reloadCart();
            } catch (error) {
                logError("Error updating cart:", error);
            }
        },
        [reloadCart]
    );

    return { handleUpdateCart };
}
